<template>
  <v-main>
    <v-container fluid class="pa-4">

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-16">
        <v-progress-circular indeterminate color="primary" size="52"></v-progress-circular>
        <div class="grey--text text-body-2 mt-3">Loading error details...</div>
      </div>

      <template v-else-if="log">

        <!-- Hero Header -->
        <div class="detail-hero mb-4" :class="heroClass">
          <!-- Back + Breadcrumb -->
          <div class="d-flex align-center mb-3">
            <v-btn icon dark small class="mr-2 back-btn" @click="$router.back()">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <span class="white--text text-caption" style="opacity:0.7; letter-spacing:1px; text-transform:uppercase;">Error Logs</span>
            <v-icon color="white" small class="mx-1" style="opacity:0.5;">mdi-chevron-right</v-icon>
            <span class="white--text text-caption" style="opacity:0.7;">Detail</span>
            <v-spacer></v-spacer>
            <v-btn
              v-if="!log.is_resolved"
              small color="white" light rounded
              @click="markResolved" :loading="resolving"
              elevation="0" class="resolve-btn"
            >
              <v-icon small left color="#2e7d32">mdi-check-circle</v-icon>
              <span style="color:#2e7d32; font-weight:700;">Mark Resolved</span>
            </v-btn>
            <div v-else class="resolved-hero-chip">
              <v-icon small class="mr-1" style="color:#a5d6a7;">mdi-check-circle</v-icon>
              <span>Resolved</span>
            </div>
          </div>

          <!-- Error type badge + message preview -->
          <div class="d-flex align-center flex-wrap mb-2" style="gap:10px;">
            <span class="error-type-hero-badge">{{ log.error_type }}</span>
            <span class="white--text font-weight-medium" style="font-size:15px; opacity:0.9; word-break:break-word; flex:1;">
              {{ log.message && log.message.length > 110 ? log.message.substring(0, 110) + '…' : log.message }}
            </span>
          </div>

          <!-- Meta strip -->
          <div class="d-flex align-center flex-wrap mt-2" style="gap:18px;">
            <div class="hero-meta-item">
              <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">mdi-alert-decagram</v-icon>
              <span class="severity-hero-chip" :class="'sev-' + log.severity">{{ log.severity }}</span>
            </div>
            <div class="hero-meta-item">
              <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">{{ log.source === 'frontend' ? 'mdi-vuejs' : 'mdi-server' }}</v-icon>
              <span>{{ log.source }}</span>
            </div>
            <div class="hero-meta-item" v-if="log.user_id || log.company_id">
              <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">mdi-account</v-icon>
              <span>User {{ log.user_id || '—' }} / Co. {{ log.company_id || '—' }}</span>
            </div>
            <div class="hero-meta-item" v-if="log.request_id">
              <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">mdi-identifier</v-icon>
              <span class="mono-text">{{ log.request_id.substring(0, 16) + '...' }}</span>
            </div>
            <div class="hero-meta-item">
              <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">mdi-clock-outline</v-icon>
              <span>{{ formatDate(log.created_at) }}</span>
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <v-row>

          <!-- Left Column: Error Details -->
          <v-col md="6">
            <v-card elevation="2" rounded="xl" class="detail-card mb-4">
              <div class="detail-card-header detail-card-header--error">
                <v-icon color="white" small class="mr-2">mdi-alert-circle</v-icon>
                <span>Error Details</span>
                <v-spacer></v-spacer>
                <span v-if="log.error_code" class="mono-chip-sm">{{ log.error_code }}</span>
              </div>
              <v-card-text class="pa-4">

                <div class="info-row">
                  <span class="info-label">Error Type</span>
                  <span class="mono-text" style="font-size:12px; color:#c62828; font-weight:700;">{{ log.error_type }}</span>
                </div>

                <div class="info-row" v-if="log.error_code">
                  <span class="info-label">Error Code</span>
                  <span class="mono-chip">{{ log.error_code }}</span>
                </div>

                <div class="info-row">
                  <span class="info-label">Severity</span>
                  <span class="severity-badge-pill" :class="'sev-pill-' + log.severity">
                    <span class="sev-dot"></span>{{ log.severity }}
                  </span>
                </div>

                <div class="info-row">
                  <span class="info-label">Source</span>
                  <div class="d-flex align-center" style="gap:5px;">
                    <v-icon x-small :color="log.source === 'frontend' ? '#7b1fa2' : '#1565c0'">
                      {{ log.source === 'frontend' ? 'mdi-vuejs' : 'mdi-server' }}
                    </v-icon>
                    <span class="source-badge-pill" :class="log.source === 'frontend' ? 'source-frontend' : 'source-backend'">
                      {{ log.source }}
                    </span>
                  </div>
                </div>

                <div class="info-row" v-if="log.user_id || log.company_id">
                  <span class="info-label">User / Company</span>
                  <div class="d-flex align-center" style="gap:6px; flex-wrap:wrap; justify-content:flex-end;">
                    <span v-if="log.user_id" class="pill-badge pill-badge--blue">
                      <v-icon x-small class="mr-1">mdi-account</v-icon>{{ log.user_id }}
                    </span>
                    <span v-if="log.company_id" class="pill-badge pill-badge--green">
                      <v-icon x-small class="mr-1">mdi-office-building</v-icon>{{ log.company_id }}
                    </span>
                  </div>
                </div>

                <div class="info-row" v-if="log.file">
                  <span class="info-label">File : Line</span>
                  <span class="mono-text" style="font-size:11px; color:#888; word-break:break-all; text-align:right; flex:1;">
                    {{ log.file }}:<strong>{{ log.line }}</strong>
                  </span>
                </div>

                <div class="info-row" style="border-bottom:none;">
                  <span class="info-label">Logged At</span>
                  <span class="info-value text-caption">{{ log.created_at }}</span>
                </div>

                <!-- Full Error Message -->
                <div class="mt-4">
                  <div class="code-block-header">
                    <span><v-icon x-small class="mr-1">mdi-message-alert</v-icon>Error Message</span>
                    <v-btn icon x-small @click="copyText(log.message)">
                      <v-icon x-small color="grey">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <div class="message-block">{{ log.message }}</div>
                </div>

              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right Column: Request Context + Linked API Log -->
          <v-col md="6">
            <v-card elevation="2" rounded="xl" class="detail-card mb-4">
              <div class="detail-card-header detail-card-header--request">
                <v-icon color="white" small class="mr-2">mdi-web</v-icon>
                <span>Request Context</span>
                <v-spacer></v-spacer>
                <span
                  v-if="log.method"
                  class="method-hero-badge method-hero-badge--sm"
                  :class="'method-' + (log.method || 'get').toLowerCase()"
                >{{ log.method }}</span>
              </div>
              <v-card-text class="pa-4">

                <div v-if="!log.method && !log.url && !log.request_id" class="text-center py-6 grey--text">
                  <v-icon color="grey lighten-1" size="36">mdi-web-off</v-icon>
                  <div class="text-caption mt-2">No request context available</div>
                </div>

                <template v-else>
                  <div class="info-row" v-if="log.url">
                    <span class="info-label">URL</span>
                    <span class="mono-text" style="font-size:11px; word-break:break-all; text-align:right; flex:1;">{{ log.url }}</span>
                  </div>

                  <div class="info-row" v-if="log.request_id">
                    <span class="info-label">Request ID</span>
                    <div class="d-flex align-center" style="gap:6px;">
                      <span class="mono-chip" style="word-break:break-all;">{{ log.request_id }}</span>
                      <v-btn icon x-small @click="copyText(log.request_id)">
                        <v-icon x-small color="grey">mdi-content-copy</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <!-- Request Body -->
                  <div v-if="log.request_body" class="mt-3">
                    <div class="code-block-header">
                      <span><v-icon x-small class="mr-1">mdi-code-json</v-icon>Request Body</span>
                      <v-btn icon x-small @click="copyText(log.request_body)">
                        <v-icon x-small color="grey">mdi-content-copy</v-icon>
                      </v-btn>
                    </div>
                    <div class="code-block">
                      <pre class="code-pre" v-html="syntaxHighlight(log.request_body)"></pre>
                    </div>
                  </div>
                </template>

              </v-card-text>
            </v-card>

            <!-- Linked API Log Card -->
            <v-card v-if="log.api_log" elevation="2" rounded="xl" class="detail-card detail-card--api-border mb-4">
              <div class="detail-card-header detail-card-header--api">
                <v-icon color="white" small class="mr-2">mdi-link-variant</v-icon>
                <span>Linked API Log</span>
                <v-spacer></v-spacer>
                <span
                  class="method-hero-badge method-hero-badge--sm"
                  :class="'method-' + (log.api_log.method || 'get').toLowerCase()"
                >{{ log.api_log.method }}</span>
              </div>
              <v-card-text class="pa-4">
                <div class="info-row">
                  <span class="info-label">URL</span>
                  <span class="mono-text" style="font-size:11px; word-break:break-all; text-align:right; flex:1;">{{ log.api_log.url }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Status</span>
                  <div class="d-flex align-center" style="gap:8px;">
                    <span class="status-badge-lg" :class="statusHeroClass(log.api_log.response_status)">{{ log.api_log.response_status }}</span>
                    <span class="grey--text text-caption">{{ statusText(log.api_log.response_status) }}</span>
                  </div>
                </div>
                <div class="info-row" style="border-bottom:none;">
                  <span class="info-label">Duration</span>
                  <span class="perf-inline" :class="log.api_log.duration_ms > 2000 ? 'perf-inline--red' : log.api_log.duration_ms > 500 ? 'perf-inline--orange' : 'perf-inline--green'">
                    <v-icon x-small class="mr-1">mdi-speedometer</v-icon>{{ log.api_log.duration_ms }}ms
                  </span>
                </div>
                <v-btn small color="blue darken-1" dark rounded class="mt-3" :to="'/api-logs/' + log.api_log.id" elevation="0">
                  <v-icon small left>mdi-arrow-right-circle</v-icon>View API Log
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Stack Trace (full width) -->
          <v-col md="12" v-if="log.trace">
            <v-card elevation="2" rounded="xl" class="detail-card mb-4">
              <div
                class="detail-card-header detail-card-header--trace"
                style="cursor:pointer;"
                @click="showTrace = !showTrace"
              >
                <v-icon color="white" small class="mr-2">mdi-file-tree</v-icon>
                <span>Stack Trace</span>
                <v-spacer></v-spacer>
                <v-btn icon x-small dark @click.stop="copyText(traceString)">
                  <v-icon x-small>mdi-content-copy</v-icon>
                </v-btn>
                <v-icon
                  small color="white" class="ml-2"
                  :style="showTrace ? 'transform:rotate(180deg); transition:transform 0.2s;' : 'transition:transform 0.2s;'"
                >mdi-chevron-down</v-icon>
              </div>
              <v-expand-transition>
                <v-card-text v-show="showTrace" class="pa-0">
                  <div class="code-block" style="border-radius:0 0 12px 12px;">
                    <pre class="code-pre" style="max-height:420px; font-size:11px;">{{ traceString }}</pre>
                  </div>
                </v-card-text>
              </v-expand-transition>
              <div v-if="!showTrace" class="trace-hint" @click="showTrace = true">
                <v-icon x-small class="mr-1" style="color:rgba(255,255,255,0.55);">mdi-eye-outline</v-icon>
                Click to expand stack trace
              </div>
            </v-card>
          </v-col>

        </v-row>

        <!-- Footer timestamp -->
        <div class="d-flex align-center mt-1 mb-2">
          <v-icon x-small color="grey" class="mr-1">mdi-clock-check-outline</v-icon>
          <span class="text-caption grey--text">Logged at {{ log.created_at }}</span>
        </div>

      </template>
    </v-container>

    <!-- Copy snackbar -->
    <v-snackbar v-model="snackbar" timeout="1500" bottom right color="grey darken-3" rounded="pill">
      <v-icon small class="mr-1">mdi-check-circle</v-icon> Copied to clipboard
    </v-snackbar>

  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "ErrorLogIdPage",
  data() {
    return {
      log: null,
      isLoading: false,
      resolving: false,
      showTrace: false,
      snackbar: false,
    };
  },
  mounted() {
    this.getData();
  },
  computed: {
    heroClass() {
      if (!this.log) return "hero--default";
      if (this.log.source === "frontend") return "hero--frontend";
      if (this.log.severity === "critical") return "hero--critical";
      if (this.log.severity === "error") return "hero--error";
      if (this.log.severity === "warning") return "hero--warning";
      return "hero--default";
    },
    traceString() {
      if (!this.log || !this.log.trace) return "";
      const t = this.log.trace;
      try { return JSON.stringify(JSON.parse(t), null, 2); } catch { return t; }
    },
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const { data } = await axios.get(`/error_logs/${this.$route.params.id}`);
        this.log = data.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async markResolved() {
      this.resolving = true;
      try {
        await axios.post(`/error_logs/${this.log.id}/resolve`);
        this.log.is_resolved = true;
      } catch (e) {
        console.error(e);
      } finally {
        this.resolving = false;
      }
    },
    statusHeroClass(status) {
      if (status >= 500) return "status-5xx";
      if (status >= 400) return "status-4xx";
      if (status >= 300) return "status-3xx";
      return "status-2xx";
    },
    statusText(status) {
      const map = {
        200: "OK", 201: "Created", 204: "No Content",
        301: "Moved Permanently", 302: "Found",
        400: "Bad Request", 401: "Unauthorized", 403: "Forbidden",
        404: "Not Found", 405: "Method Not Allowed", 422: "Unprocessable Entity",
        500: "Internal Server Error", 502: "Bad Gateway", 503: "Service Unavailable",
      };
      return map[status] || "";
    },
    syntaxHighlight(str) {
      if (!str) return "";
      const raw = typeof str === "string" ? str : JSON.stringify(str);
      const TRUNC = "...[TRUNCATED]";
      const isTruncated = raw.endsWith(TRUNC);
      const parseTarget = isTruncated ? raw.slice(0, -TRUNC.length) : raw;
      let pretty;
      try {
        pretty = JSON.stringify(JSON.parse(parseTarget), null, 2);
        if (isTruncated) pretty += "\n" + TRUNC;
      } catch {
        pretty = this.basicFormatJson(raw);
      }
      const escaped = pretty
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      return escaped.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        (match) => {
          let cls = "json-number";
          if (/^"/.test(match)) {
            cls = /:$/.test(match) ? "json-key" : "json-string";
          } else if (/true|false/.test(match)) {
            cls = "json-boolean";
          } else if (/null/.test(match)) {
            cls = "json-null";
          }
          return `<span class="${cls}">${match}</span>`;
        }
      );
    },
    basicFormatJson(str) {
      let indent = 0;
      let inString = false;
      let result = "";
      for (let i = 0; i < str.length; i++) {
        const ch = str[i];
        const prev = str[i - 1];
        if (ch === '"' && prev !== "\\") { inString = !inString; }
        if (inString) { result += ch; continue; }
        switch (ch) {
          case "{": case "[":
            result += ch + "\n" + "  ".repeat(++indent);
            break;
          case "}": case "]":
            result += "\n" + "  ".repeat(--indent) + ch;
            break;
          case ",":
            result += ch + "\n" + "  ".repeat(indent);
            break;
          case ":":
            result += ": ";
            break;
          default:
            result += ch;
        }
      }
      return result;
    },
    copyText(text) {
      navigator.clipboard.writeText(text).then(() => { this.snackbar = true; }).catch(() => {});
    },
    formatDate(dt) {
      if (!dt) return "-";
      return new Date(dt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "medium" });
    },
  },
};
</script>

<style scoped>
/* ===== Hero Header ===== */
.detail-hero {
  border-radius: 16px;
  padding: 22px 24px 18px;
}
.hero--critical { background: linear-gradient(135deg, #7f0000 0%, #b71c1c 50%, #c62828 100%); }
.hero--error    { background: linear-gradient(135deg, #bf360c 0%, #e64a19 50%, #f4511e 100%); }
.hero--warning  { background: linear-gradient(135deg, #e65100 0%, #ef6c00 50%, #f57c00 100%); }
.hero--frontend { background: linear-gradient(135deg, #4a148c 0%, #6a1b9a 50%, #7b1fa2 100%); }
.hero--default  { background: linear-gradient(135deg, #37474f 0%, #455a64 50%, #546e7a 100%); }

.back-btn { background: rgba(255,255,255,0.15) !important; }

.error-type-hero-badge {
  display: inline-block;
  padding: 4px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.5px;
  background: rgba(255,255,255,0.2);
  color: #ffffff;
  border: 1.5px solid rgba(255,255,255,0.4);
  font-family: 'Courier New', monospace;
  white-space: nowrap;
  flex-shrink: 0;
}

.severity-hero-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  background: rgba(255,255,255,0.22);
  color: #fff;
}

.hero-meta-item {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: rgba(255,255,255,0.75);
}

.resolved-hero-chip {
  display: inline-flex;
  align-items: center;
  padding: 5px 16px;
  background: rgba(255,255,255,0.15);
  border: 1.5px solid rgba(255,255,255,0.3);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(255,255,255,0.9);
}

.resolve-btn {
  border-radius: 20px !important;
  font-size: 12px !important;
}

/* ===== Detail Cards ===== */
.detail-card { overflow: hidden; }
.detail-card--api-border { border: 1.5px solid #90caf9 !important; }

.detail-card-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: white;
}
.detail-card-header--error   { background: linear-gradient(90deg, #b71c1c, #e53935); }
.detail-card-header--request { background: linear-gradient(90deg, #1565c0, #1976d2); }
.detail-card-header--api     { background: linear-gradient(90deg, #0277bd, #039be5); }
.detail-card-header--trace   { background: linear-gradient(90deg, #37474f, #546e7a); }

/* ===== Method Badges (dark background contexts) ===== */
.method-hero-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.5px;
}
.method-hero-badge--sm { padding: 2px 9px; font-size: 11px; }
.method-get    { background: rgba(255,255,255,0.2); color: #ffffff; border: 1.5px solid rgba(255,255,255,0.4); }
.method-post   { background: rgba(255,255,255,0.2); color: #ffffff; border: 1.5px solid rgba(255,255,255,0.4); }
.method-patch  { background: rgba(255,255,255,0.2); color: #ffffff; border: 1.5px solid rgba(255,255,255,0.4); }
.method-put    { background: rgba(255,255,255,0.2); color: #ffffff; border: 1.5px solid rgba(255,255,255,0.4); }
.method-delete { background: rgba(255,255,255,0.2); color: #ffffff; border: 1.5px solid rgba(255,255,255,0.4); }

/* ===== Status Badges ===== */
.status-badge-lg {
  display: inline-block;
  padding: 4px 14px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}
.status-badge-lg.status-2xx { background: #e8f5e9; color: #2e7d32; }
.status-badge-lg.status-3xx { background: #e3f2fd; color: #1565c0; }
.status-badge-lg.status-4xx { background: #fff3e0; color: #e65100; }
.status-badge-lg.status-5xx { background: #fce4ec; color: #c62828; }

/* ===== Info Rows ===== */
.info-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 9px 0;
  border-bottom: 1px solid #f0f0f0;
  gap: 12px;
}
.info-label {
  font-size: 11px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  min-width: 100px;
  flex-shrink: 0;
  margin-top: 2px;
}
.info-value { font-size: 13px; color: #333; text-align: right; flex: 1; }

/* ===== Severity Badge Pills ===== */
.severity-badge-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  gap: 5px;
}
.sev-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.85;
  flex-shrink: 0;
}
.sev-pill-critical { background: #fce4ec; color: #c62828; }
.sev-pill-error    { background: #fff3e0; color: #e65100; }
.sev-pill-warning  { background: #fffde7; color: #f9a825; }

/* ===== Source Badge Pills ===== */
.source-badge-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: capitalize;
}
.source-backend  { background: #e3f2fd; color: #1565c0; }
.source-frontend { background: #f3e5f5; color: #6a1b9a; }

/* ===== Pill Badges ===== */
.pill-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}
.pill-badge--blue  { background: #e3f2fd; color: #1565c0; }
.pill-badge--green { background: #e8f5e9; color: #2e7d32; }

/* ===== Mono Chips ===== */
.mono-chip {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  background: #f0f0f0;
  color: #444;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.3px;
}
.mono-chip-sm {
  font-family: 'Courier New', monospace;
  font-size: 10px;
  background: rgba(255,255,255,0.2);
  color: #fff;
  padding: 2px 8px;
  border-radius: 6px;
}
.mono-text {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #555;
}

/* ===== Error Message Block ===== */
.message-block {
  background: #fff8f8;
  border: 1px solid #ffcdd2;
  border-left: 4px solid #e53935;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  color: #c62828;
  line-height: 1.6;
  word-break: break-word;
}

/* ===== Code Blocks ===== */
.code-block-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
}
.code-block {
  background: #1e1e2e;
  border-radius: 10px;
  padding: 1px;
}
.code-pre {
  background: transparent;
  color: #cdd6f4;
  font-family: 'Courier New', 'Monaco', monospace;
  font-size: 11.5px;
  line-height: 1.6;
  padding: 14px;
  overflow: auto;
  max-height: 280px;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
}

/* ===== Stack Trace ===== */
.trace-hint {
  padding: 7px 16px;
  font-size: 11px;
  color: rgba(255,255,255,0.55);
  cursor: pointer;
  text-align: center;
  background: #455a64;
  border-radius: 0 0 12px 12px;
  user-select: none;
  transition: background 0.15s;
}
.trace-hint:hover { background: #546e7a; }

/* ===== Performance Inline Badges ===== */
.perf-inline {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}
.perf-inline--green  { background: #e8f5e9; color: #2e7d32; }
.perf-inline--orange { background: #fff3e0; color: #e65100; }
.perf-inline--red    { background: #fce4ec; color: #c62828; }

/* ===== JSON Syntax Highlighting (Catppuccin Mocha) ===== */
.code-pre :deep(.json-key)     { color: #89dceb; font-weight: 600; }
.code-pre :deep(.json-string)  { color: #a6e3a1; }
.code-pre :deep(.json-number)  { color: #fab387; }
.code-pre :deep(.json-boolean) { color: #cba6f7; font-weight: 700; }
.code-pre :deep(.json-null)    { color: #f38ba8; font-weight: 700; }
</style>
